/**
 * SES Identity Service - Frontend wrapper for SES domain verification
 * Calls Firebase Functions to register and verify domains in AWS SES
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Types
export interface SESVerificationResult {
    success: boolean;
    verificationToken: string;
    dkimTokens: string[];
    dnsRecords: SESDNSRecord[];
}

export interface SESDNSRecord {
    type: 'TXT' | 'CNAME';
    name: string;
    value: string;
    purpose: string;
}

export interface SESStatusResult {
    success: boolean;
    isVerified: boolean;
    verificationStatus: string;
    dkimStatus: string;
    dkimEnabled: boolean;
}

class SESIdentityService {
    private registerDomainFunction: ReturnType<typeof httpsCallable>;
    private checkStatusFunction: ReturnType<typeof httpsCallable>;
    private deleteDomainFunction: ReturnType<typeof httpsCallable>;

    constructor() {
        this.registerDomainFunction = httpsCallable(functions, 'registerDomainInSES');
        this.checkStatusFunction = httpsCallable(functions, 'checkSESDomainStatus');
        this.deleteDomainFunction = httpsCallable(functions, 'deleteDomainFromSES');
    }

    /**
     * Register a domain in AWS SES and get DNS records to configure
     * @param domain - The domain to register (e.g., "example.com")
     * @returns Verification token, DKIM tokens, and DNS records to add
     */
    async registerDomain(domain: string): Promise<SESVerificationResult> {
        try {
            console.log(`📧 Registering domain in SES: ${domain}`);
            const result = await this.registerDomainFunction({ domain });
            const data = result.data as SESVerificationResult;
            console.log('✅ Domain registered in SES:', data);
            return data;
        } catch (error: any) {
            console.error('❌ Error registering domain in SES:', error);
            throw new Error(error.message || 'Failed to register domain in SES');
        }
    }

    /**
     * Check if a domain is verified in AWS SES
     * @param domain - The domain to check
     * @returns Verification status
     */
    async checkStatus(domain: string): Promise<SESStatusResult> {
        try {
            console.log(`🔍 Checking SES status for: ${domain}`);
            const result = await this.checkStatusFunction({ domain });
            const data = result.data as SESStatusResult;
            console.log('📋 SES status:', data);
            return data;
        } catch (error: any) {
            console.error('❌ Error checking SES status:', error);
            throw new Error(error.message || 'Failed to check domain status');
        }
    }

    /**
     * Delete a domain identity from AWS SES
     * @param domain - The domain to delete
     */
    async deleteDomain(domain: string): Promise<void> {
        try {
            console.log(`🗑️ Deleting domain from SES: ${domain}`);
            await this.deleteDomainFunction({ domain });
            console.log('✅ Domain deleted from SES');
        } catch (error: any) {
            console.error('❌ Error deleting domain from SES:', error);
            throw new Error(error.message || 'Failed to delete domain from SES');
        }
    }

    /**
     * Helper to determine if domain is fully verified (both identity and DKIM)
     */
    isFullyVerified(status: SESStatusResult): boolean {
        return status.isVerified && status.dkimStatus === 'Success';
    }
}

export const sesIdentityService = new SESIdentityService();
export default sesIdentityService;
